// Workbench Macros — record & replay (Jun 2026, Randy's idea).
//
// A creator turns on Record, performs their normal one-video workflow (pull a
// transcript, run an operation, route results to staging, etc.), and stops.
// The recorded *semantic* steps (not mouse clicks) are saved by name. Later they
// press ▶ and the macro re-drives the real Workbench UI live, with recorded
// pacing, a floating Pause/Resume shelf, and "Pause for editing" checkpoints.
//
// Design notes:
//  - The player calls imperative primitives on `busRef.current` (Workbench) and
//    `ytApiRef.current` (YouTube tool). Both refs are refreshed every render by
//    their owners, so the player always reads/writes fresh React state.
//  - N=1 (default, safest): the macro ends with the finished data sitting in the
//    staging area — the user reviews and commits manually. Batch (2–10) + Boost
//    auto-arm are the next increment.
import { useEffect, useRef, useState } from "react";
import {
  Play, Pause, Stop, Circle, Trash, PencilSimple, CaretDown, CaretRight,
  FloppyDisk, X, Sparkle, Record as RecordIcon, ArrowUp, ArrowDown, Bell, Eye, EyeSlash, Check,
  Lightning, ArrowClockwise, Warning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import LockGate from "@/components/LockGate";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useEntitlements } from "@/lib/entitlements";
import {
  HEAVY_BOOST_MODELS, SMALL_BOOST_MODELS, boostModelLabel, boostModelMeta,
  isHeavyBoostModel, boostCostEstimateCents,
} from "@/components/BoostButton";
import { toast } from "sonner";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const clampDelay = (ms) => Math.min(8000, Math.max(300, ms || 600));

// Jul 2026 (Randy) — skip/halt reason wording for the line-by-line skip report.
const REASON_LABEL = {
  NO_TRANSCRIPT: "No transcript exists",
  BLANK: "Blank transcript",
  UNAVAILABLE: "Video unavailable / private",
  FETCH_FAILED: "Transcript fetch failed",
  QUOTA: "YouTube quota exceeded",
  KEY: "YouTube API key problem",
  VOUCHER: "Silo voucher empty",
  RUN_FAILED: "The operation failed",
  EMPTY_OUTPUT: "No output was produced",
  UNKNOWN: "Skipped (unexpected error)",
};
// These stop the whole batch — retrying the next video won't help until the
// creator acts (fix the key, add quota, top up the voucher).
const HALT_CODES = new Set(["QUOTA", "KEY", "VOUCHER"]);

export default function WorkbenchMacros({
  busRef, ytApiRef, macroApiRef, mode, setMode,
  recordingRef, recordBufRef, lastStepAtRef, recordCount, setRecordCount,
  siloId,
}) {
  const [macros, setMacros] = useState([]);
  const { locked: cpLocked, lockStyle: cpLockStyle } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();
  const macroLocked = cpLocked("macro_autobuild");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [countByMacro, setCountByMacro] = useState({});
  const [tierByMacro, setTierByMacro] = useState({});
  const [lastBatch, setLastBatch] = useState(null);
  const [skipPausesByMacro, setSkipPausesByMacro] = useState({});
  const [notifyByMacro, setNotifyByMacro] = useState({});
  const [savingSettingsId, setSavingSettingsId] = useState(null);
  const [editStep, setEditStep] = useState(null); // { macroId, idx, type, pct, command_id, command_name }
  const [skipReport, setSkipReport] = useState([]); // [{id,title,reason,code,chars,libId,video_id}] — persisted per silo
  const [heavyConfirm, setHeavyConfirm] = useState(null); // { macro, estCents, model, videos } — premium-model cost gate
  const [doneModal, setDoneModal] = useState(null); // { title, message } — "run complete" acknowledgement

  // Live playback status for the floating shelf.
  const [play, setPlay] = useState({ name: "", idx: 0, total: 0, editing: false });
  const pausedRef = useRef(false);
  const abortRef = useRef(false);
  // Jul 2026 (Randy) — "Follow along": glide the page to each action's spot just
  // before it happens, so a running macro plays like a guided tour. This is
  // GENERIC by step TYPE — it works for ANY macro built now or in the future
  // from these steps, never hardcoded to one recipe. Toggle lives on the floating
  // play shelf; default ON; remembered across sessions.
  const [followScroll, setFollowScroll] = useState(() => {
    try { return localStorage.getItem("macro_follow_scroll") !== "0"; } catch { return true; }
  });
  const followRef = useRef(followScroll);
  useEffect(() => {
    followRef.current = followScroll;
    try { localStorage.setItem("macro_follow_scroll", followScroll ? "1" : "0"); } catch { /* ignore */ }
  }, [followScroll]);
  // Semantic step → the on-screen spot its change lands. Purely visual; any
  // unmapped/future step type simply stays put (returns null).
  const anchorSelectorForStep = (step) => {
    const p = step?.params || {};
    switch (step?.type) {
      case "yt_fetch": return '[data-testid="yt-results-pane"]';
      case "yt_transfer": {
        const t = p.target;
        if (t === "assistant") return '[data-testid="ai-buddy-mount-workbench"]';
        if (typeof t === "string" && t.startsWith("slot")) return `[data-testid="slot-${t.replace("slot", "")}"]`;
        return '[data-testid="staging-area"]';
      }
      case "set_output_size": return '[data-testid="output-size-control"]';
      case "run": return '[data-testid="workbench-run"]';
      case "append_result_to_staging": return '[data-testid="staging-area"]';
      case "send_result_to_slot": return `[data-testid="slot-${Number(p.n) || 1}"]`;
      case "send_staging_to_slot": return `[data-testid="slot-${Number(p.n) || 1}"]`;
      default: return null;
    }
  };
  // Which label/button shimmers during this step's phase.
  const shimmerSelectorForStep = (step) => {
    const p = step?.params || {};
    switch (step?.type) {
      case "yt_fetch":
        return p.kind === "metadata" ? '[data-testid="yt-pull-metadata"]'
          : p.kind === "comments" ? '[data-testid="yt-pull-comments"]'
          : '[data-testid="yt-pull-transcript"]';
      case "yt_transfer":
        return p.target === "staging" ? '[data-testid="wb-staging-label"]' : anchorSelectorForStep(step);
      case "set_output_size": return '[data-testid="wb-output-size-label"]';
      case "run": return '[data-testid="workbench-run"]';
      case "append_result_to_staging": return '[data-testid="wb-staging-label"]';
      case "send_result_to_slot":
      case "send_staging_to_slot": return `[data-testid="slot-${Number(p.n) || 1}"]`;
      default: return null;
    }
  };
  // All DOM lookups are scoped to the Workbench root so we never grab the
  // hidden Staging-tab twins (which share staging-area / staging-textarea ids).
  const wbRoot = () => document.querySelector('[data-testid="workbench-tab"]') || document;
  const $wb = (sel) => { try { return sel ? wbRoot().querySelector(sel) : null; } catch { return null; } };
  const scrollToSelector = (sel, block = "center") => {
    if (!followRef.current || !sel) return;
    try { $wb(sel)?.scrollIntoView({ behavior: "smooth", block }); } catch { /* noop */ }
  };
  const shimmerOn = (sel) => { const el = $wb(sel); if (el) el.classList.add("macro-shimmer"); };
  const shimmerOff = (sel) => { const el = $wb(sel); if (el) el.classList.remove("macro-shimmer"); };
  const shimmerClearAll = () => { try { wbRoot().querySelectorAll(".macro-shimmer").forEach((e) => e.classList.remove("macro-shimmer")); } catch { /* noop */ } };
  // Smoothly animate an element's internal scroll (used for the staging box's
  // own scrollbar creeping to the new bottom so you watch content arrive).
  const animateScroll = (el, to, ms) => new Promise((resolve) => {
    if (!el) return resolve();
    const from = el.scrollTop;
    const dist = to - from;
    if (Math.abs(dist) < 2 || ms <= 0) { el.scrollTop = to; return resolve(); }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      const eased = 0.5 - Math.cos(t * Math.PI) / 2; // ease in-out
      el.scrollTop = from + dist * eased;
      if (t < 1 && !abortRef.current) requestAnimationFrame(tick);
      else { el.scrollTop = to; resolve(); }
    };
    requestAnimationFrame(tick);
  });
  const stagingTextarea = () => $wb('textarea[data-testid="staging-textarea"]');
  const creepStagingToBottom = async (ms) => {
    const ta = stagingTextarea();
    if (!ta) return;
    await sleep(180); // let the freshly-added text render so scrollHeight is real
    await animateScroll(ta, ta.scrollHeight, ms);
  };

  // Choreograph ONE step: glide to it → shimmer → arrive → run it → watch the
  // change land. Generic by step type, so every macro (now & future) gets the
  // tour. Follow OFF = fast path (recorded pacing, no scroll/shimmer/dwell).
  const choreographStep = async (step, opts) => {
    const type = step?.type;
    const p = step?.params || {};
    if (!followRef.current) {
      shimmerClearAll();
      await sleep(clampDelay(step.delay_ms));
      await executeStep(step, opts);
      return;
    }
    // Clear the PREVIOUS phase's shimmer, then light THIS one. We never clear at
    // the end of a step — so the shimmer stays lit through the whole step and
    // while the session is paused, right up until the next step lights up. Only
    // the run's finally block clears everything when playback fully ends.
    shimmerClearAll();

    // Pause step: stay put, but keep the STAGING AREA shimmering so it's obvious
    // the session is live and waiting on you to edit + resume.
    if (type === "pause_for_editing") {
      shimmerOn('[data-testid="wb-staging-label"]');
      scrollToSelector('[data-testid="staging-area"]');
      await executeStep(step, opts); // blocks until the user resumes
      return;
    }

    const shimmer = shimmerSelectorForStep(step);
    scrollToSelector(anchorSelectorForStep(step));
    if (shimmer) shimmerOn(shimmer);
    await sleep(1000); // travel + a beat, so it's clearly "arrived"
    if (abortRef.current) return;

    if (type === "run") {
      // Kick the run; Run button keeps shimmering while it works.
      const runP = executeStep(step, opts);
      await Promise.race([runP, sleep(5000)]); // move to results at 5s OR when done
      shimmerOff(shimmer);
      scrollToSelector('[data-testid="workbench-output"]');
      shimmerOn('[data-testid="result-label"]'); // stays lit until the next step
      await runP;            // resolves once the answer has landed
      await sleep(1200);     // let it be seen
      return;
    }

    const isStagingDrop = type === "append_result_to_staging" || (type === "yt_transfer" && p.target === "staging");
    if (isStagingDrop) {
      // Sit the staging bar at its CURRENT bottom, add the data, then creep to
      // the NEW bottom over 2s (top→bottom on the first, empty fill). STAGING
      // AREA keeps shimmering the whole time.
      const ta = stagingTextarea();
      if (ta) ta.scrollTop = Math.max(0, ta.scrollHeight - ta.clientHeight);
      await executeStep(step, opts);
      await creepStagingToBottom(2000);
      await sleep(1000);
      return;
    }

    // Default (fetch title/transcript/comments, set size, send-to-slot): fire,
    // then dwell ~1s so the change registers. Shimmer stays lit until next step.
    await executeStep(step, opts);
    await sleep(1000);
  };

  const load = async () => {
    try {
      const r = await api.get("/workbench/macros?surface=workbench");
      const list = r.data || [];
      setMacros(list);
      // Seed the per-macro run settings from what was SAVED (so leaving the page
      // or Clear Workbench never resets them).
      const c = {}, t = {}, sp = {}, nf = {};
      for (const m of list) {
        c[m.id] = m.run_count || 1;
        t[m.id] = m.tier || 2;
        sp[m.id] = m.skip_pauses !== false; // default ON
        nf[m.id] = !!m.notify_on_finish;
      }
      setCountByMacro(c);
      setTierByMacro(t);
      setSkipPausesByMacro(sp);
      setNotifyByMacro(nf);
    } catch {
      /* ignore — section just shows empty */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Feb 17, 2026 (Randy pt-macro-selected) — the currently-selected macro
  // is the one the new "Run selected Macro" button on the YouTube Tool
  // card uses. Backend stamps `is_selected:true` on exactly one row (or
  // none if the user has zero macros). Randy's rule: "one is always
  // selected" — the backend also handles the fallback-to-earliest.
  const selectedMacro = macros.find((m) => m.is_selected) || macros[0] || null;
  const selectedMacroId = selectedMacro?.id || null;

  // Click a macro row → prompt to change the default. First-ever pick
  // skips the confirm (there's nothing to overwrite). Clicking the
  // ALREADY-selected row is a no-op (Randy: "can only switch, never
  // deselect"). Confirm popup is deliberately a window.confirm — Randy
  // approved this as sufficient friction.
  const selectMacro = async (macro) => {
    if (!macro || macro.id === selectedMacroId) return;
    if (selectedMacroId) {
      const ok = window.confirm(
        `Change currently selected Macro to be your default?\n\nCurrent: ${selectedMacro?.name || "(none)"}\nNew: ${macro.name}`
      );
      if (!ok) return;
    }
    try {
      await api.post(`/workbench/macros/${macro.id}/select?surface=workbench`);
      setMacros((ms) => ms.map((m) => ({ ...m, is_selected: m.id === macro.id })));
      toast.success(`"${macro.name}" is now your default macro.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update selection.");
    }
  };

  // Expose bus methods for the YouTube Tool's "Run selected Macro" button.
  // Re-registers every render so the closures always see fresh state (the
  // same pattern YouTubeTool uses with `macroApi.registerApi`).
  const runMacroRef = useRef(null);
  useEffect(() => {
    if (!macroApiRef) return;
    macroApiRef.current = {
      getSelectedMacro: () => selectedMacro,
      runSelectedMacroOnUrl: async (urlOverride) => {
        if (!selectedMacro) { toast.error("No macro selected."); return; }
        // Feb 17, 2026 (Randy pt-run-selected-macro-clear-staging-only). Just
        // clear STAGING before the run — matches how batch mode (wanted > 1)
        // already clears between videos. DO NOT bump the child clear-nonce
        // (that clears the URL field / pull area / output, which pulled the
        // rug out from under the macro). Slots + Result get overwritten by
        // the macro steps anyway; only staging needs to start clean.
        try { busRef.current?.clearStaging?.(); } catch { /* noop */ }
        // Feb 17, 2026 (Randy pt-run-selected-macro-fix) — hand the URL
        // to runMacro as `opts.singleUrl`. The engine then:
        //   • forces wanted=1 (ignores the macro row's count)
        //   • skips the getNextUnstruckVideo preflight (no library skip)
        //   • uses the provided URL as the authoritative target
        //   • ensures strike-through after (idempotent)
        // The green Run button on each macro row still uses count/batch —
        // this override is exclusive to the top button.
        await runMacroRef.current?.(selectedMacro, { singleUrl: urlOverride });
      },
    };
  });

  // ---------- Skip report (per-silo, persisted) ----------
  // A skipped/errored video stays STRUCK in the library below and appears here.
  // "Re-attempt" un-strikes it (re-queues for a later run); "Delete" just drops
  // the row and leaves it struck (creator chose to ignore it).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`mysilo_macro_skips_${siloId || "none"}`);
      setSkipReport(raw ? JSON.parse(raw) : []);
    } catch { setSkipReport([]); }
  }, [siloId]);
  const persistSkips = (list) => {
    setSkipReport(list);
    try { localStorage.setItem(`mysilo_macro_skips_${siloId || "none"}`, JSON.stringify(list)); } catch { /* ignore */ }
  };
  const reattemptSkip = (item) => {
    try {
      if (item.libId && item.video_id) (ytApiRef.current || {}).unStrikeVideo?.(item.libId, item.video_id);
    } catch { /* ignore */ }
    persistSkips(skipReport.filter((s) => s.id !== item.id));
    toast.success(`“${(item.title || "video").slice(0, 40)}” un-struck — it'll be picked up next run.`);
  };
  const deleteSkip = (item) => {
    persistSkips(skipReport.filter((s) => s.id !== item.id));
  };

  // Does this macro replay on a Boost, and is that a premium "heavy-hitter" model?
  const macroBoostStep = (macro) => (macro.steps || []).find((s) => s.type === "run" && s.params?.boost?.target_model);
  const macroIsHeavy = (macro) => {
    const b = macroBoostStep(macro);
    return b ? isHeavyBoostModel(b.params.boost.target_model) : false;
  };

  // ---------- Recording ----------
  const startRecording = () => {
    if (macroLocked) { cpOpenGate("macro_autobuild"); return; }
    if (mode !== "idle") return;
    recordBufRef.current = [];
    lastStepAtRef.current = 0;
    recordingRef.current = true;
    setRecordCount(0);
    setPendingSave(false);
    setMode("recording");
    toast.success("Recording… run your workflow on one video, then Stop.");
  };
  const stopRecording = () => {
    recordingRef.current = false;
    setMode("idle");
    const steps = recordBufRef.current || [];
    if (!steps.length) {
      toast.message("Nothing recorded — no actions were captured.");
      return;
    }
    setSaveName("");
    setPendingSave(true);
  };
  const discardRecording = () => {
    recordingRef.current = false;
    recordBufRef.current = [];
    setRecordCount(0);
    setPendingSave(false);
    setMode("idle");
    toast.message("Recording discarded.");
  };
  const saveMacro = async () => {
    const name = saveName.trim();
    if (!name) { toast.error("Give your macro a name."); return; }
    const steps = recordBufRef.current || [];
    if (!steps.length) { setPendingSave(false); return; }
    setSaving(true);
    try {
      await api.post("/workbench/macros", { name, steps, surface: "workbench" });
      toast.success(`Macro "${name}" saved · ${steps.length} steps.`);
      recordBufRef.current = [];
      setRecordCount(0);
      setPendingSave(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save macro.");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Playback ----------
  const waitWhilePaused = async () => {
    while (pausedRef.current && !abortRef.current) await sleep(200);
  };
  const executeStep = async (step, opts = {}) => {
    const bus = busRef.current || {};
    const yt = ytApiRef.current || {};
    switch (step.type) {
      case "yt_fetch":
        if (yt.fetch) await yt.fetch(step.params?.kind || "transcript");
        break;
      case "yt_transfer":
        if (yt.transfer) await yt.transfer(step.params?.target || "staging");
        break;
      case "set_output_size":
        bus.setOutputSize?.(step.params?.pct ?? null);
        break;
      case "run":
        // Jul 2026 (Randy) — Auto-Stage is forced ON during a macro, so the Run
        // processes ANY size in as many passes as needed. No more "too large for
        // a single pass" skip; only real errors (no transcript / voucher / quota)
        // are surfaced by runOperation, and the loop classifies skip-vs-halt.
        if (step.params?.command_id) bus.selectOperation?.(step.params.command_id);
        await sleep(250);
        await bus.runOperation?.();
        break;
      case "append_result_to_staging":
        // With Auto-Stage on, runAllSlots already streamed the result into
        // staging — skip this step so we don't double-append.
        if (bus.getAutoStage?.()) break;
        bus.appendResultToStaging?.();
        break;
      case "send_result_to_slot":
        bus.sendResultToSlot?.(Number(step.params?.n) || 1);
        break;
      case "send_staging_to_slot":
        bus.sendStagingToSlot?.(Number(step.params?.n) || 1);
        break;
      case "pause_for_editing":
        if (opts.skipPauses) break; // batch "Skip pauses" toggle — glide past
        pausedRef.current = true;
        setMode("paused");
        setPlay((p) => ({ ...p, editing: true }));
        await waitWhilePaused();
        setPlay((p) => ({ ...p, editing: false }));
        break;
      default:
        break;
    }
  };
  const runMacro = async (macro, opts = {}) => {
    if (macroLocked) { cpOpenGate("macro_autobuild"); return; }
    if (mode !== "idle") return;
    const steps = macro.steps || [];
    if (!steps.length) { toast.error("This macro has no steps."); return; }
    // Feb 17, 2026 (Randy pt-run-selected-macro-fix) — the YouTube Tool's
    // "Run selected Macro" button passes `opts.singleUrl`. Semantics:
    //   • force wanted = 1 (batch/count settings on the macro row apply
    //     only to the green Run button on the macro card itself)
    //   • skip the `getNextUnstruckVideo` preflight entirely — the
    //     provided URL is authoritative, even if it's already struck
    //   • use `yt.setUrl(opts.singleUrl)` right before the run
    //   • do NOT auto-strike during the run (respect current state)
    //   • AFTER the run, call `yt.ensureVideoStruckByUrl(opts.singleUrl)`
    //     — idempotent, only sets struck=true if not already; if the URL
    //     doesn't match any library row, it's a silent no-op.
    const singleUrl = typeof opts.singleUrl === "string" ? opts.singleUrl.trim() : "";
    const wanted = singleUrl ? 1 : Math.max(1, Math.min(50, countByMacro[macro.id] || 1));
    const tier = tierByMacro[macro.id] || 2;
    const batch = wanted > 1;
    // Heavy-hitter cost gate — fires on a single OR batch run BEFORE anything
    // starts. Whatever they pick after being warned is on them.
    const boostStep = macroBoostStep(macro);
    if (boostStep && isHeavyBoostModel(boostStep.params.boost.target_model) && !opts.heavyConfirmed) {
      const b = boostStep.params.boost;
      const ratio = b.desired_output_chars ? Math.min(1, Math.max(0.05, b.desired_output_chars / 25000)) : 0.3;
      const perVideo = boostCostEstimateCents(b.target_model, 25000, ratio);
      setHeavyConfirm({ macro, estCents: perVideo * wanted, model: boostModelLabel(b.target_model), videos: wanted });
      return;
    }

    // Jun 2026 (Randy) — pauses are now skipped on EVERY batch (N>1) run no
    // matter what the toggle says ("that should be auto off no matter what for
    // a group run"), and the toggle governs SINGLE runs, which is what it was
    // created for. Before this it read `batch && toggle`, so a single run
    // always stopped at a pause step and the checkbox did nothing.
    const skipPauses = batch || skipPausesByMacro[macro.id] !== false;
    const notify = batch && !!notifyByMacro[macro.id];
    const yt = ytApiRef.current || {};
    const bus = busRef.current || {};
    const usesYouTube = steps.some((s) => s.type === "yt_fetch" || s.type === "yt_transfer");

    // Pre-flight: bail cleanly before entering "playing" if there's nothing to run on.
    // singleUrl runs skip this entirely — the URL is the authority.
    if (usesYouTube && !singleUrl) {
      const first = yt.getNextUnstruckVideo?.();
      if (!first) {
        const st = yt.getState?.() || {};
        if (!st.url) { toast.error("No video to process: no un-struck videos in the library and no URL loaded. Fetch a channel's library (or paste a URL) first."); return; }
        if (batch) { toast.error("Batch needs a channel library with un-struck videos — fetch a channel first."); return; }
      }
    }
    // singleUrl: load the URL into the tool BEFORE we enter "playing" so
    // the first step's yt_fetch reads the right URL.
    if (singleUrl) {
      try { yt.loadVideo?.(singleUrl); } catch { /* noop */ }
    }

    // Auto-Stage forced ON for the whole run (reverted in finally) so any-size
    // video processes in as many passes as needed. Arm the macro's Boost (if it
    // recorded one) as a persistent session for the batch (cancelled in finally).
    const prevAutoStage = bus.getAutoStage?.() ?? true;
    bus.setAutoStage?.(true);
    let boostArmed = false;
    if (boostStep) {
      try { await bus.armBoostForMacro?.(boostStep.params.boost, wanted); boostArmed = true; }
      catch { toast.error("Couldn't arm the macro's Boost — running on the default model."); }
    }

    pausedRef.current = false;
    abortRef.current = false;
    setMode("playing");
    setLastBatch(null);
    const committed = [];
    const skippedNow = [];
    let processed = 0;
    let stopMsg = null;
    let lastTitle = "";
    // `processedIds` = every video already handled this batch (committed OR
    // skipped-too-large). We pass it to getNextUnstruckVideo as an exclude-set so
    // the loop always advances UPWARD past them — even a skipped (un-struck) video
    // won't be handed back, and we don't depend on the optimistic strike flushing.
    const processedIds = new Set();
    const nextFreshVideo = () => (ytApiRef.current || {}).getNextUnstruckVideo?.(processedIds) || null;
    try {
      for (let v = 0; v < wanted; v++) {
        if (abortRef.current) break;
        let targetVideo = null;
        if (usesYouTube && !singleUrl) {
          targetVideo = nextFreshVideo();
          if (!targetVideo) {
            const st = (ytApiRef.current || {}).getState?.() || {};
            if (v === 0 && st.url && !batch) {
              // single manual-URL run — allowed
            } else {
              stopMsg = v === 0 ? "no un-struck videos to process" : "reached the top — no more un-struck videos";
              break;
            }
          }
          if (targetVideo) {
            // Follow-along: glide to the next un-struck video, send its URL up
            // AND strike it NOW so you SEE it cross off, hold a beat, then the
            // first step rides up to the pull area. (Decision (a): if this video
            // later fails or is skipped-too-large, we un-strike it below so
            // nothing is ever lost.)
            if (followRef.current) { scrollToSelector(`[data-testid="yt-lib-row-${targetVideo.video_id}"]`); await sleep(900); }
            (ytApiRef.current || {}).loadVideo?.(targetVideo.url);
            (ytApiRef.current || {}).strikeVideo?.(targetVideo.libId, targetVideo.video_id);
            processedIds.add(targetVideo.video_id);
            await sleep(followRef.current ? 3000 : 500); // hold ~3s so the strike-through is clearly seen
          }
        }
        // Jun 2026 (Randy) — a YouTube macro always starts from clean staging,
        // single run included, so one video's output can never be committed
        // on top of the previous one's. Non-YouTube macros keep whatever the
        // creator had staged (a macro can legitimately act on existing text).
        if (batch || usesYouTube) { bus.clearStaging?.(); await sleep(150); }
        lastTitle = targetVideo?.title || "";
        setPlay({ name: macro.name, idx: 0, total: steps.length, editing: false, video: lastTitle, batchIdx: v + 1, batchTotal: wanted });
        if (targetVideo) toast.message(`${batch ? `(${v + 1}/${wanted}) ` : ""}Processing “${lastTitle.slice(0, 48)}”…`);

        try {
          for (let i = 0; i < steps.length; i++) {
            await waitWhilePaused();
            if (abortRef.current) break;
            setPlay((p) => ({ ...p, idx: i + 1, stepLabel: steps[i].label || steps[i].type }));
            // Full guided-tour choreography for THIS step (glide → shimmer →
            // arrive → run → watch it land). Generic by step type. Follow OFF
            // falls back to the recorded pacing with no movement.
            await choreographStep(steps[i], { skipPauses, batch });
            if (abortRef.current) break;
          }
          if (abortRef.current) {
            // Stopped mid-video: it was struck early but never committed —
            // un-strike it so it's not lost (decision (a) applies to Stop too).
            if (targetVideo) (ytApiRef.current || {}).unStrikeVideo?.(targetVideo.libId, targetVideo.video_id);
            break;
          }

          // Jun 2026 (Randy) — EVERY run commits, batch or single. It used to
          // be batch-only: a single run stopped with a "review your staging
          // area" popup, which creators read as "it's saved". Start the next
          // video and the workbench is wiped — the video was struck, marked
          // processed and monitored, while its content never reached the silo.
          // The inverse of a coverage hole, and silent. Now `processed` only
          // increments on a real commit, so the strike + ledger + monitor
          // chain (fired below for singleUrl runs) can never run ahead of the
          // content. "There really is no point in reviewing the data before
          // sending it" — review happens in the silo Library, which can edit
          // and delete.
          let staged = "";
          for (let t = 0; t < 15; t++) {
            // The last step's setStaging needs a React render before it lands
            // in the bus's fresh-state ref, so poll briefly — otherwise the
            // commit reads a stale/empty value.
            staged = (bus.getState?.().staging || "").trim();
            if (staged) break;
            await sleep(200);
          }
          if (!staged) {
            // Nothing was produced (empty/blank source or an AI hiccup). Never
            // commit an empty doc — SKIP it and keep going. Batch keeps the
            // video struck for the skip report (Re-attempt un-strikes it), but
            // the ledger row goes: nothing landed, so it must not claim it did.
            skippedNow.push({ id: `${targetVideo?.video_id || v}-${Date.now()}`, title: lastTitle || "Untitled video", reason: REASON_LABEL.EMPTY_OUTPUT, code: "EMPTY_OUTPUT", chars: 0, libId: targetVideo?.libId, video_id: targetVideo?.video_id });
            if (targetVideo?.video_id) {
              try { await (ytApiRef.current || {}).markUnprocessed?.(targetVideo.video_id); }
              catch { /* gap alerts still catch it */ }
            }
            bus.clearStaging?.();
            if (followRef.current) await sleep(400);
          } else {
            const ytState = (ytApiRef.current || {}).getState?.() || {};
            // Jun 2026 (Randy) — "its naming them 'macro result, with the date'
            // when it should be the top line of the file." A single run has no
            // library row to take a title from, and if the macro never touched
            // the YouTube tool there is no pulled title either, so the date
            // stamp was all that was left. The result's own first line is a
            // better name than a date and is almost always the heading the AI
            // wrote. The date stays as the last resort for a result that opens
            // with no usable line at all.
            const topLine = (() => {
              const first = staged.split("\n")
                .map((l) => l.replace(/^#+\s*/, "").replace(/^\*+|\*+$/g, "").trim())
                .find((l) => l.length >= 3) || "";
              return first.length <= 160 ? first : `${first.slice(0, 120).trim()}…`;
            })();
            const title = lastTitle || ytState.pullTitle || topLine
              || `Macro result ${new Date().toISOString().slice(0, 10)}`;
            const runStep = [...steps].reverse().find((s) => s.type === "run");
            const operation = runStep?.params?.command_name || null;
            const docId = await bus.commitStagingDirect?.({ tier, title, operation });
            committed.push({ docId, title, libId: targetVideo?.libId, video_id: targetVideo?.video_id });
            processed++;
            lastTitle = title;
            toast.success(batch
              ? `Sent to your silo · Tier ${tier} (${committed.length}/${wanted}).`
              : `Sent to your silo · Tier ${tier}.`);
            if (followRef.current) { scrollToSelector('[data-testid="staging-area"]'); await sleep(450); }
            bus.clearStaging?.();
            if (followRef.current) await sleep(1000); // see it disappear before the next video
          }
        } catch (stepErr) {
          shimmerClearAll();
          // Jul 2026 (Randy) — two-bucket classification. The video STAYS STRUCK
          // either way and lands in the skip report (Re-attempt un-strikes it).
          const code = stepErr?.__yt_code || stepErr?.code || "UNKNOWN";
          const reason = REASON_LABEL[code] || stepErr?.reason || stepErr?.message || "Skipped";
          skippedNow.push({ id: `${targetVideo?.video_id || v}-${Date.now()}`, title: lastTitle || "Untitled video", reason, code, chars: 0, libId: targetVideo?.libId, video_id: targetVideo?.video_id });
          bus.clearStaging?.();
          if (HALT_CODES.has(code)) {
            // Systemic — retrying the next video won't help. Stop the batch.
            stopMsg = `stopped: ${reason}`;
            break;
          }
          // Otherwise it's a per-video skip — keep going to the next video.
        }
        if (!usesYouTube) break; // a non-YouTube macro only makes sense once
      }
    } finally {
      const aborted = abortRef.current;
      pausedRef.current = false;
      abortRef.current = false;
      shimmerClearAll();
      // Revert Auto-Stage to the user's prior state + cancel the macro's Boost.
      try { bus.setAutoStage?.(prevAutoStage); } catch { /* ignore */ }
      if (boostArmed) { try { await bus.cancelBoostForMacro?.(); } catch { /* ignore */ } }
      setMode("idle");
      setPlay({ name: "", idx: 0, total: 0, editing: false, video: "", batchIdx: 0, batchTotal: 0, stepLabel: "" });
      // Merge this run's skips into the persistent per-silo report (newest first).
      if (skippedNow.length) persistSkips([...skippedNow, ...skipReport]);
      // Feb 17, 2026 (Randy pt-run-selected-macro-fix) → Jun 2026. For a
      // singleUrl run, record "this video is in the silo" AFTER the commit:
      // ledger → library row (created if missing) → struck → monitored. It
      // only fires when something was actually committed (`processed` now
      // increments on a real commit only), so the chain can never claim a
      // video is in the silo when its content never landed.
      if (singleUrl && processed) {
        try {
          const ytFresh = ytApiRef.current || {};
          const marked = await ytFresh.ensureVideoStruckByUrl?.(singleUrl);
          const markedId = marked?.video_id;
          if (markedId) {
            // Undo has to be able to take the mark back too, or an undone
            // commit would leave the video claiming to be processed.
            for (const it of committed) {
              if (!it.video_id) { it.video_id = markedId; it.markedProcessed = true; }
            }
          }
        } catch { /* silent — a queued retry will land it later */ }
      }
      if (batch) {
        if (committed.length) setLastBatch({ macroName: macro.name, tier, items: committed });
        if (notify) fireFinishNotification(macro.name, committed.length, skippedNow.length, tier);
        const skipNote = skippedNow.length ? ` Skipped ${skippedNow.length} (see the skip report — they're left struck).` : "";
        if (aborted) {
          toast.message(`Batch stopped by you — ${committed.length} committed so far.`);
        } else {
          // Decision (b): one popup at the very end summarizing the whole batch.
          setDoneModal({
            title: stopMsg ? "Batch stopped" : "Batch complete",
            message: `Committed & struck ${committed.length} video${committed.length === 1 ? "" : "s"} to Tier ${tier}.${skipNote}${stopMsg ? ` (${stopMsg})` : ""}`,
          });
        }
      } else if (aborted) {
        toast.message("Macro stopped.");
      } else if (stopMsg) {
        toast.error(`Macro ${stopMsg}.`);
      } else if (processed) {
        // Single run — now actually committed (Jun 2026), so say so. Undo is
        // available for it too, same as a batch.
        if (committed.length) setLastBatch({ macroName: macro.name, tier, items: committed });
        setDoneModal({
          title: "Sent to your silo",
          message: lastTitle
            ? `“${lastTitle.slice(0, 60)}” is in your silo at Tier ${tier}, and the video is struck through. Edit or delete it any time from your Library.`
            : `Done — committed to your silo at Tier ${tier}. Edit or delete it any time from your Library.`,
        });
      } else if (skippedNow.length) {
        toast.message(`Skipped — ${skippedNow[0].reason}. It's listed in the skip report and left struck.`);
      }
    }
  };
  // Keep the bus's `runSelectedMacroOnUrl` calling into the LATEST closure.
  runMacroRef.current = runMacro;
  const undoLastBatch = async () => {
    if (!lastBatch) return;
    const bus = busRef.current || {};
    const yt = ytApiRef.current || {};
    let undone = 0;
    for (const it of lastBatch.items) {
      try {
        if (it.docId) await bus.deleteDoc?.(it.docId);
        if (it.libId && it.video_id) yt.unStrikeVideo?.(it.libId, it.video_id);
        // A single-URL run has no library row of its own; the mark still has
        // to come off or the video would keep claiming to be processed.
        else if (it.markedProcessed && it.video_id) await yt.markUnprocessed?.(it.video_id);
        undone++;
      } catch { /* keep going */ }
    }
    toast.success(`Undid ${undone} commit${undone === 1 ? "" : "s"} — docs deleted and videos un-struck.`);
    setLastBatch(null);
  };
  const pausePlay = () => { pausedRef.current = true; setMode("paused"); };
  const resumePlay = () => { pausedRef.current = false; setMode("playing"); };
  const stopPlay = () => { abortRef.current = true; pausedRef.current = false; };

  // ---------- Per-macro run settings (Save button — Randy Jul 2 2026) ----------
  // Persists the run count, target tier, and Skip-pauses toggle onto the macro so
  // leaving the page or Clear Workbench can never reset them.
  const saveMacroSettings = async (macro) => {
    setSavingSettingsId(macro.id);
    try {
      const body = {
        run_count: Math.max(1, Math.min(50, countByMacro[macro.id] || 1)),
        tier: tierByMacro[macro.id] || 2,
        skip_pauses: skipPausesByMacro[macro.id] !== false,
        notify_on_finish: !!notifyByMacro[macro.id],
      };
      const r = await api.patch(`/workbench/macros/${macro.id}`, body);
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
      toast.success("Macro settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings.");
    } finally {
      setSavingSettingsId(null);
    }
  };

  // ---------- "Notify me when the batch finishes" (browser notification) ----------
  const toggleNotify = (macroId, checked) => {
    setNotifyByMacro((n) => ({ ...n, [macroId]: checked }));
    if (!checked) return;
    if (!("Notification" in window)) {
      toast.message("This browser can't show notifications. On iPhone, add the silo to your Home Screen first.");
      return;
    }
    if (Notification.permission === "denied") {
      toast.message("Notifications are blocked in your browser settings — turn them on to get a batch-finished ping.");
    } else if (Notification.permission === "default") {
      Notification.requestPermission().then((p) => {
        if (p !== "granted") toast.message("Allow notifications to get pinged when a batch finishes.");
      }).catch(() => {});
    }
  };
  const fireFinishNotification = (macroName, committedN, skippedN, tier) => {
    try {
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      const body = `Committed ${committedN} to Tier ${tier}${skippedN ? ` · skipped ${skippedN} too-large` : ""}.`;
      const n = new Notification(`Batch finished — ${macroName}`, { body, tag: `macro-${macroName}` });
      n.onclick = () => { try { window.focus(); n.close(); } catch { /* ignore */ } };
    } catch { /* notifications are best-effort */ }
  };

  // ---------- Smart step editing (output-size + operation choice) ----------
  const stepLabelFor = (type, params) => {
    if (type === "set_output_size") return `Set output size → ${params.pct == null ? "Auto" : params.pct + "%"}`;
    if (type === "run") return `Run "${params.command_name || "operation"}"`;
    return type;
  };
  const openStepEdit = (macro, idx, step) => {
    setEditStep({
      macroId: macro.id,
      idx,
      type: step.type,
      pct: step.type === "set_output_size" ? (step.params?.pct ?? null) : null,
      command_id: step.type === "run" ? (step.params?.command_id || "") : "",
      command_name: step.params?.command_name || "",
      boost: step.type === "run" ? (step.params?.boost || null) : null,
    });
  };
  const saveStepEdit = async (macro, idx, newParams) => {
    const steps = (macro.steps || []).map((s, i) => {
      if (i !== idx) return s;
      const params = { ...s.params, ...newParams };
      return { ...s, params, label: stepLabelFor(s.type, params) };
    });
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { steps });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
      setEditStep(null);
      toast.success("Step updated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update step.");
    }
  };

  // ---------- Editing saved macros ----------
  const deleteMacro = async (macro) => {
    try {
      await api.delete(`/workbench/macros/${macro.id}`);
      setMacros((ms) => ms.filter((m) => m.id !== macro.id));
      toast.success(`Deleted "${macro.name}".`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete macro.");
    }
  };
  const deleteStep = async (macro, idx) => {
    const nextSteps = (macro.steps || []).filter((_, i) => i !== idx);
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { steps: nextSteps });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update macro.");
    }
  };
  const moveStep = async (macro, idx, dir) => {
    const steps = [...(macro.steps || [])];
    const j = idx + dir;
    if (j < 0 || j >= steps.length) return;
    [steps[idx], steps[j]] = [steps[j], steps[idx]];
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { steps });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reorder step.");
    }
  };
  const saveRename = async (macro) => {
    const name = renameVal.trim();
    if (!name) { setRenameId(null); return; }
    try {
      const r = await api.patch(`/workbench/macros/${macro.id}`, { name });
      setMacros((ms) => ms.map((m) => (m.id === macro.id ? r.data : m)));
      setRenameId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not rename macro.");
    }
  };

  const recording = mode === "recording";
  const playingOrPaused = mode === "playing" || mode === "paused";

  return (
    <div className="mt-8" data-testid="workbench-macros">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={18} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-2xl text-[#1C1B1A]">Macros</h3>
        <span className="text-xs text-[#6B6A68]">Record your workflow once, replay it on the next video.</span>
      </div>

      {/* Record controls */}
      <div className="bg-white border hairline rounded-xl p-4 mb-4" data-testid="macro-record-panel">
        {!recording && !pendingSave && (
          <LockGate locked={macroLocked} lockStyle={cpLockStyle} onLockedClick={() => cpOpenGate("macro_autobuild")}>
          <button
            onClick={startRecording}
            disabled={playingOrPaused}
            data-testid="macro-record-start"
            className="text-sm px-4 py-2 rounded-md border hairline bg-white hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-2 disabled:opacity-50"
          >
            <RecordIcon size={16} weight="fill" className="text-[#C16E5A]" /> Record a macro
          </button>
          </LockGate>
        )}

        {recording && (
          <div className="flex flex-wrap items-center gap-3" data-testid="macro-recording-bar">
            <span className="flex items-center gap-1.5 text-sm text-[#C16E5A] font-medium">
              <Circle size={12} weight="fill" className="animate-pulse" /> Recording · {recordCount} step{recordCount === 1 ? "" : "s"}
            </span>
            <span className="text-xs text-[#6B6A68]">Do your workflow (typing anywhere adds a “Pause for editing” step).</span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={stopRecording} data-testid="macro-record-stop"
                className="text-sm px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5">
                <Stop size={14} weight="fill" /> Stop &amp; save
              </button>
              <button onClick={discardRecording} data-testid="macro-record-discard"
                className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Discard</button>
            </div>
          </div>
        )}

        {pendingSave && (
          <div className="flex flex-wrap items-center gap-2" data-testid="macro-save-row">
            <span className="text-sm text-[#1C1B1A]">Name this macro:</span>
            <input
              autoFocus
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveMacro(); }}
              placeholder="e.g. Transcript → condense → stage"
              data-testid="macro-save-name"
              className="flex-1 min-w-[220px] bg-[#FDFCF8] border hairline rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            <button onClick={saveMacro} disabled={saving} data-testid="macro-save-confirm"
              className="text-sm px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
              <FloppyDisk size={14} /> Save
            </button>
            <button onClick={discardRecording} data-testid="macro-save-cancel"
              className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Cancel</button>
          </div>
        )}
      </div>

      {/* Last-batch recap + one-click undo */}
      {lastBatch && (
        <div className="bg-[#FDF6E9] border hairline rounded-xl p-3 mb-4" data-testid="macro-last-batch">
          <div className="text-xs text-[#6B6A68] mb-2">
            {lastBatch.items.length === 1 ? "Last run" : "Last batch"} — <span className="text-[#1C1B1A] font-medium">“{lastBatch.macroName}”</span> committed {lastBatch.items.length} doc{lastBatch.items.length === 1 ? "" : "s"} to Tier {lastBatch.tier} &amp; struck {lastBatch.items.length === 1 ? "the video" : "their videos"}.
          </div>
          <ul className="text-[11px] text-[#6B6A68] mb-3 space-y-0.5 max-h-32 overflow-y-auto">
            {lastBatch.items.map((it, i) => (
              <li key={i} className="truncate" data-testid={`macro-batch-item-${i}`}>• {it.title}</li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <button onClick={undoLastBatch} data-testid="macro-undo-batch"
              className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#C16E5A] hover:bg-[#FDF1ED] hover:border-[#C16E5A] flex items-center gap-1.5">
              <Trash size={13} /> Undo {lastBatch.items.length === 1 ? "it" : "all"} (delete doc{lastBatch.items.length === 1 ? "" : "s"} + un-strike)
            </button>
            <button onClick={() => setLastBatch(null)} data-testid="macro-batch-dismiss"
              className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">Dismiss</button>
          </div>
        </div>
      )}

      {/* Skip report — line-by-line, per-silo, persisted. Skipped/errored videos
          stay STRUCK in the library below; Re-attempt un-strikes one for a later
          run, Delete drops the row and leaves it struck. */}
      {skipReport.length > 0 && (
        <div className="bg-[#FDF1ED] border hairline rounded-xl p-3 mb-4" data-testid="macro-skip-report">
          <div className="text-xs text-[#C16E5A] font-medium mb-1 flex items-center gap-1.5">
            <Warning size={14} weight="fill" />
            Skip report · {skipReport.length} video{skipReport.length === 1 ? "" : "s"} left struck
          </div>
          <div className="text-[11px] text-[#6B6A68] mb-2">
            These were skipped (or halted the batch). They stay struck below. <span className="font-medium">Re-attempt</span> un-strikes one so a later run picks it up; <span className="font-medium">Delete</span> keeps it struck and just clears the row.
          </div>
          <ul className="space-y-1.5 max-h-56 overflow-y-auto">
            {skipReport.map((it) => (
              <li key={it.id} className="flex items-center gap-2 bg-white border hairline rounded-md px-2.5 py-1.5" data-testid={`macro-skip-item-${it.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#1C1B1A] truncate" data-testid={`macro-skip-title-${it.id}`}>{it.title || "Untitled video"}</div>
                  <div className="text-[10px] text-[#C16E5A]" data-testid={`macro-skip-reason-${it.id}`}>
                    {it.reason}{it.chars ? ` · ${Number(it.chars).toLocaleString()} chars` : ""}
                  </div>
                </div>
                <button onClick={() => reattemptSkip(it)} data-testid={`macro-skip-reattempt-${it.id}`}
                  title="Un-strike this video so the next run picks it up"
                  className="text-[11px] px-2 py-1 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#EAF3EC] hover:border-[#4A5D4E] flex items-center gap-1 shrink-0">
                  <ArrowClockwise size={12} /> Re-attempt
                </button>
                <button onClick={() => deleteSkip(it)} data-testid={`macro-skip-delete-${it.id}`}
                  title="Remove from the report and keep it struck"
                  className="text-[11px] px-2 py-1 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] flex items-center gap-1 shrink-0">
                  <Trash size={12} /> Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Saved macros list */}
      {loading ? (
        <div className="text-xs text-[#6B6A68]">Loading macros…</div>
      ) : macros.length === 0 ? (
        <div className="text-xs text-[#6B6A68] italic" data-testid="macro-empty">
          No macros yet. Hit “Record a macro”, run through one video, and save it.
        </div>
      ) : (
        <div className="space-y-2" data-testid="macro-list">
          {macros.map((m) => {
            const isOpen = expandedId === m.id;
            const isSelected = m.id === selectedMacroId;
            const handleRowClick = (e) => {
              // Any click that landed on a real control (button / input) is
              // that control's job — don't hijack it as a selection change.
              if (e.target.closest("button, input")) return;
              selectMacro(m);
            };
            return (
              <div
                key={m.id}
                onClick={handleRowClick}
                className={`bg-white border hairline rounded-xl overflow-hidden cursor-pointer transition-colors ${isSelected ? "bg-gradient-to-r from-[#EAF3EC]/40 via-[#EAF3EC]/70 to-[#EAF3EC]/40 ring-1 ring-[#4A5D4E]/30" : "hover:bg-[#FDFCF8]"}`}
                data-testid={`macro-item-${m.id}`}
                title={isSelected ? "This is your currently-selected macro (the 'Run selected Macro' button on the YouTube Tool uses it)." : "Click to make this your currently-selected macro."}
              >
                <div className="flex items-center gap-2 px-4 py-3">
                  <button
                    onClick={() => runMacro(m)}
                    disabled={mode !== "idle"}
                    data-testid={`macro-run-${m.id}`}
                    className="text-sm px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                  >
                    <Play size={14} weight="fill" /> Run
                  </button>
                  {renameId === m.id ? (
                    <input
                      autoFocus
                      value={renameVal}
                      onChange={(e) => setRenameVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(m); if (e.key === "Escape") setRenameId(null); }}
                      onBlur={() => saveRename(m)}
                      data-testid={`macro-rename-input-${m.id}`}
                      className="flex-1 bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                    />
                  ) : (
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1C1B1A] font-medium truncate flex items-center gap-1.5">
                        <span className="truncate">{m.name}</span>
                        {macroBoostStep(m) && (
                          <span
                            data-testid={`macro-boost-badge-${m.id}`}
                            title={macroIsHeavy(m) ? `Boosted with ${boostModelLabel(macroBoostStep(m).params.boost.target_model)} — a premium model. Costs add up per video.` : `Boosted with ${boostModelLabel(macroBoostStep(m).params.boost.target_model)}.`}
                            className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full ${macroIsHeavy(m) ? "bg-[#7A5200] text-white" : "bg-[#EAF3EC] text-[#4A5D4E]"}`}
                          >
                            <Lightning size={9} weight="fill" /> {macroIsHeavy(m) ? "Heavy" : "Boost"}
                          </span>
                        )}
                      </div>
                      <button onClick={() => setExpandedId(isOpen ? null : m.id)} data-testid={`macro-editsteps-${m.id}`}
                        className="text-[11px] text-[#6B6A68] hover:text-[#4A5D4E] underline decoration-dotted underline-offset-2">
                        {(m.steps || []).length} step{(m.steps || []).length === 1 ? "" : "s"} · {isOpen ? "hide" : "view / edit steps"}
                      </button>
                    </div>
                  )}
                  {/* Feb 17, 2026 (Randy pt-macro-selected) — "SELECTED" pill,
                      shown between the name and the row controls. Signals to
                      the user which macro the YouTube Tool's "Run selected
                      Macro" button will fire. */}
                  {isSelected && (
                    <span
                      data-testid={`macro-selected-${m.id}`}
                      className="shrink-0 text-[9px] uppercase tracking-[0.28em] font-semibold text-[#2F6B3A] bg-[#EAF3EC] border border-[#B9D4BE] rounded-full px-2 py-0.5"
                      title="This macro is currently SELECTED — the YouTube Tool's Run selected Macro button will use it."
                    >
                      Selected
                    </span>
                  )}
                  <button onClick={() => { setRenameId(m.id); setRenameVal(m.name); }} data-testid={`macro-rename-${m.id}`}
                    title="Rename" className="p-1.5 text-[#6B6A68] hover:text-[#4A5D4E]"><PencilSimple size={15} /></button>
                  <button onClick={() => setExpandedId(isOpen ? null : m.id)} data-testid={`macro-expand-${m.id}`}
                    title="View steps" className="p-1.5 text-[#6B6A68] hover:text-[#4A5D4E]">{isOpen ? <CaretDown size={15} /> : <CaretRight size={15} />}</button>
                  <button onClick={() => deleteMacro(m)} data-testid={`macro-delete-${m.id}`}
                    title="Delete macro" className="p-1.5 text-[#6B6A68] hover:text-[#C16E5A]"><Trash size={15} /></button>
                </div>

                {isOpen && (
                  <div className="border-t hairline bg-[#FDFCF8] px-4 py-3" data-testid={`macro-steps-${m.id}`}>
                    {macroIsHeavy(m) && (
                      <div className="mb-2.5 flex items-start gap-2 text-[11px] bg-[#FFF7E6] border border-[#F0D9A0] rounded-md px-2.5 py-2 text-[#7A5200]" data-testid={`macro-heavy-warning-${m.id}`}>
                        <Lightning size={14} weight="fill" className="mt-0.5 shrink-0" />
                        <span>
                          This macro runs on <span className="font-medium">{boostModelLabel(macroBoostStep(m).params.boost.target_model)}</span>, a premium model. You're billed for <span className="font-medium">every pass of every video</span> — on a batch of {(countByMacro[m.id] || 1)} that adds up fast. Consider a cheaper "Hitters" model (edit the Run step below) for large batches.
                        </span>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-[#6B6A68]">
                      <span>Run through</span>
                      <input
                        type="number" min={1} max={50}
                        value={countByMacro[m.id] || 1}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1));
                          setCountByMacro((c) => ({ ...c, [m.id]: v }));
                        }}
                        data-testid={`macro-count-${m.id}`}
                        className="w-16 bg-white border hairline rounded-md px-2 py-1 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                      />
                      <span>video(s)</span>
                      {/* Jun 2026 (Randy) — the tier picker and the Skip-pauses
                          toggle used to appear only at count > 1. Since every
                          run now auto-commits, a single run needs to SEE which
                          tier it lands in; and the Skip-pauses box governs
                          single runs (batches always skip), which is what it
                          was created for — it was unreachable at count 1. */}
                      <span>→ auto-commit to</span>
                      <select
                        value={tierByMacro[m.id] || 2}
                        onChange={(e) => setTierByMacro((t) => ({ ...t, [m.id]: parseInt(e.target.value, 10) }))}
                        data-testid={`macro-tier-${m.id}`}
                        className="bg-white border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                      >
                        <option value={1}>Tier 1</option>
                        <option value={2}>Tier 2</option>
                        <option value={3}>Tier 3</option>
                      </select>
                      <label className="flex items-center gap-1.5 cursor-pointer ml-1" data-testid={`macro-skip-pauses-label-${m.id}`}>
                        <input
                          type="checkbox"
                          checked={skipPausesByMacro[m.id] !== false}
                          onChange={(e) => setSkipPausesByMacro((sp) => ({ ...sp, [m.id]: e.target.checked }))}
                          disabled={(countByMacro[m.id] || 1) > 1}
                          data-testid={`macro-skip-pauses-${m.id}`}
                        />
                        <span title={(countByMacro[m.id] || 1) > 1
                          ? "Batches always skip pause steps — nothing would be there to resume them."
                          : "Glide past this macro's “Pause for editing” steps on a single run."}>
                          Skip “Pause for editing” steps
                          {(countByMacro[m.id] || 1) > 1 && " (always on for batches)"}
                        </span>
                      </label>
                      {(countByMacro[m.id] || 1) > 1 && (
                        <>
                          <label className="flex items-center gap-1.5 cursor-pointer ml-1" data-testid={`macro-notify-label-${m.id}`}>
                            <input
                              type="checkbox"
                              checked={!!notifyByMacro[m.id]}
                              onChange={(e) => toggleNotify(m.id, e.target.checked)}
                              data-testid={`macro-notify-${m.id}`}
                            />
                            <Bell size={12} weight="fill" className="text-[#4A5D4E]" />
                            <span>Notify me when it finishes</span>
                          </label>
                        </>
                      )}
                      <button
                        onClick={() => saveMacroSettings(m)}
                        disabled={savingSettingsId === m.id}
                        data-testid={`macro-save-settings-${m.id}`}
                        className="ml-auto text-xs px-3 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                      >
                        <FloppyDisk size={12} /> {savingSettingsId === m.id ? "Saving…" : "Save"}
                      </button>
                    </div>
                    <div className="text-[11px] text-[#6B6A68] mb-2 italic">
                      {(countByMacro[m.id] || 1) > 1
                        ? "Batch: Auto-stage is forced ON, so each video is processed in as many passes as it needs (any size). It auto-commits each result to the tier above, strikes the video, and moves on. Pause steps are always skipped in a batch — nothing would be there to resume them. A video with no transcript / unavailable / blank is skipped (left struck) and listed in the skip report — the batch keeps going. It only STOPS on a systemic problem (quota, bad key, empty voucher). Undo the whole batch anytime."
                        : "Single run: takes the next un-struck video (bottom of the library, upward) — or the URL in the YouTube Tool — then auto-commits the result to the tier above, strikes the video, and marks it processed. Review or delete it afterwards in your silo Library. Tick Skip pauses to glide past this macro's pause steps. Reorder steps with ↑↓, edit choice-steps with ✎, remove with ✕. Remember to Save if you change the count/tier/pauses."}
                    </div>
                    <ol className="space-y-1.5">
                      {(m.steps || []).map((s, i) => {
                        const editable = s.type === "set_output_size" || s.type === "run";
                        const editing = editStep && editStep.macroId === m.id && editStep.idx === i;
                        return (
                          <li key={i} className="text-xs" data-testid={`macro-step-${m.id}-${i}`}>
                            <div className="flex items-center gap-1.5">
                              <span className="w-5 text-right text-[#9A9890] tabular-nums">{i + 1}.</span>
                              <span className={`flex-1 ${s.type === "pause_for_editing" ? "text-[#C16E5A]" : "text-[#1C1B1A]"}`}>
                                {s.type === "pause_for_editing" && <Pause size={11} weight="fill" className="inline mr-1" />}
                                {s.label || s.type}
                              </span>
                              {editable && (
                                <button onClick={() => (editing ? setEditStep(null) : openStepEdit(m, i, s))} data-testid={`macro-step-edit-${m.id}-${i}`}
                                  title="Edit this step's choice" className="p-1 text-[#9A9890] hover:text-[#4A5D4E]"><PencilSimple size={12} /></button>
                              )}
                              <button onClick={() => moveStep(m, i, -1)} disabled={i === 0} data-testid={`macro-step-up-${m.id}-${i}`}
                                title="Move up" className="p-1 text-[#9A9890] hover:text-[#4A5D4E] disabled:opacity-30"><ArrowUp size={12} /></button>
                              <button onClick={() => moveStep(m, i, 1)} disabled={i === (m.steps || []).length - 1} data-testid={`macro-step-down-${m.id}-${i}`}
                                title="Move down" className="p-1 text-[#9A9890] hover:text-[#4A5D4E] disabled:opacity-30"><ArrowDown size={12} /></button>
                              <button onClick={() => deleteStep(m, i)} data-testid={`macro-step-delete-${m.id}-${i}`}
                                title="Delete this step" className="p-1 text-[#9A9890] hover:text-[#C16E5A]"><X size={12} /></button>
                            </div>
                            {editing && (
                              <div className="mt-1.5 ml-6 p-2.5 bg-white border hairline rounded-md flex flex-wrap items-center gap-2" data-testid={`macro-step-editor-${m.id}-${i}`}>
                                {editStep.type === "set_output_size" ? (
                                  <>
                                    <label className="flex items-center gap-1 text-xs text-[#6B6A68]">
                                      <input type="checkbox" checked={editStep.pct == null}
                                        onChange={(e) => setEditStep((es) => ({ ...es, pct: e.target.checked ? null : 50 }))}
                                        data-testid={`macro-step-edit-auto-${m.id}-${i}`} />
                                      Auto
                                    </label>
                                    {editStep.pct != null && (
                                      <>
                                        <input type="range" min={5} max={100} value={editStep.pct}
                                          onChange={(e) => setEditStep((es) => ({ ...es, pct: parseInt(e.target.value, 10) }))}
                                          data-testid={`macro-step-edit-slider-${m.id}-${i}`} className="flex-1 min-w-[120px]" />
                                        <span className="text-xs tabular-nums w-10 text-right">{editStep.pct}%</span>
                                      </>
                                    )}
                                    <button onClick={() => saveStepEdit(m, i, { pct: editStep.pct })} data-testid={`macro-step-edit-save-${m.id}-${i}`}
                                      className="text-xs px-3 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">Save</button>
                                    <button onClick={() => setEditStep(null)} data-testid={`macro-step-edit-cancel-${m.id}-${i}`}
                                      className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Cancel</button>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-xs text-[#6B6A68]">Operation:</span>
                                    <select
                                      value={editStep.command_id || ""}
                                      onChange={(e) => {
                                        const id = e.target.value;
                                        const ops = busRef.current?.getCommands?.() || [];
                                        const op = ops.find((o) => o.id === id);
                                        setEditStep((es) => ({ ...es, command_id: id, command_name: op?.name || "" }));
                                      }}
                                      data-testid={`macro-step-edit-op-${m.id}-${i}`}
                                      className="flex-1 min-w-[160px] bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                                    >
                                      {(busRef.current?.getCommands?.() || []).map((o) => (
                                        <option key={o.id} value={o.id}>{o.name}</option>
                                      ))}
                                    </select>
                                    {/* Boost model for this Run step (Randy Jul 2026): change the LLM the
                                        macro replays on. "Heavy Hitters" are premium/expensive. */}
                                    <div className="w-full flex flex-wrap items-center gap-2 mt-1.5 pt-1.5 border-t border-[#E0DED8]">
                                      <span className="text-xs text-[#6B6A68] flex items-center gap-1"><Lightning size={12} weight="fill" className="text-[#7A5200]" /> Boost:</span>
                                      <select
                                        value={editStep.boost?.target_model || ""}
                                        onChange={(e) => {
                                          const id = e.target.value;
                                          if (!id) { setEditStep((es) => ({ ...es, boost: null })); return; }
                                          const meta = boostModelMeta(id);
                                          setEditStep((es) => ({ ...es, boost: {
                                            target_model: id,
                                            target_provider: meta?.provider || null,
                                            target_max_tokens: meta?.max_output_tokens || null,
                                            desired_output_chars: es.boost?.desired_output_chars ?? null,
                                          } }));
                                        }}
                                        data-testid={`macro-step-edit-boost-model-${m.id}-${i}`}
                                        className="flex-1 min-w-[160px] bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                                      >
                                        <option value="">No boost — default model</option>
                                        <optgroup label="Hitters (cheaper)">
                                          {SMALL_BOOST_MODELS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                        </optgroup>
                                        <optgroup label="Heavy Hitters (premium)">
                                          {HEAVY_BOOST_MODELS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                        </optgroup>
                                      </select>
                                      {editStep.boost?.target_model && isHeavyBoostModel(editStep.boost.target_model) && (
                                        <span className="text-[10px] text-[#7A5200] flex items-center gap-0.5" data-testid={`macro-step-edit-boost-premium-${m.id}-${i}`}><Warning size={10} weight="fill" /> premium — costs add up per video</span>
                                      )}
                                    </div>
                                    <button onClick={() => saveStepEdit(m, i, { command_id: editStep.command_id, command_name: editStep.command_name, boost: editStep.boost || null })} data-testid={`macro-step-edit-save-${m.id}-${i}`}
                                      className="text-xs px-3 py-1 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">Save</button>
                                    <button onClick={() => setEditStep(null)} data-testid={`macro-step-edit-cancel-${m.id}-${i}`}
                                      className="text-xs text-[#6B6A68] hover:text-[#C16E5A]">Cancel</button>
                                  </>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Floating Pause/Resume shelf — visible while a macro is playing (impersonate-style). */}
      {playingOrPaused && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[120] w-[92vw] max-w-lg bg-[#1C1B1A] text-[#FDFCF8] rounded-xl shadow-2xl px-4 py-3 flex flex-col gap-2"
          data-testid="macro-shelf"
        >
          {/* Current-step readout — the same wording as the macro's edit-steps list. */}
          {!play.editing && play.idx > 0 && (
            <div className="w-full flex items-center gap-2 text-[11px] text-[#E8D9B0] border-b border-white/10 pb-1.5" data-testid="macro-shelf-step">
              <span className="shrink-0 tabular-nums px-1.5 py-0.5 rounded bg-white/10">Step {play.idx}/{play.total}</span>
              <span className="truncate font-medium">{play.stepLabel || "…"}</span>
            </div>
          )}
          <div className="w-full flex items-center gap-3">
          <span className="flex items-center gap-2 min-w-0">
            {mode === "paused"
              ? <Pause size={18} weight="fill" className="text-[#E8B04B] shrink-0" />
              : <Play size={18} weight="fill" className="text-[#8FB08C] shrink-0 animate-pulse" />}
            <span className="min-w-0">
              <span className="block text-sm font-medium truncate">{play.name || "Macro"}</span>
              <span className="block text-[11px] text-[#B9B7B0]">
                {play.editing
                  ? "Editing paused — make your changes, then Resume"
                  : `${play.batchTotal > 1 ? `Video ${play.batchIdx}/${play.batchTotal} · ` : ""}Step ${play.idx} of ${play.total}${play.video ? ` · ${play.video.slice(0, 30)}` : ""}`}
              </span>
            </span>
          </span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <button
              onClick={() => setFollowScroll((v) => !v)}
              data-testid="macro-shelf-follow"
              title={followScroll ? "Follow along is ON — the page glides to each action. Click to stop the auto-scrolling." : "Follow along is OFF — scroll freely. Click to let the page follow the action again."}
              className={`text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5 transition-colors ${followScroll ? "bg-[#8FB08C] text-[#1C1B1A] hover:bg-[#7CA079]" : "bg-white/10 text-[#B9B7B0] hover:bg-white/20"}`}
            >
              {followScroll ? <Eye size={14} weight="fill" /> : <EyeSlash size={14} weight="duotone" />}
              <span className="hidden sm:inline">Follow{followScroll ? "" : " off"}</span>
            </button>
            {mode === "paused" ? (
              <button onClick={resumePlay} data-testid="macro-shelf-resume"
                className="text-sm px-3 py-1.5 rounded-md bg-[#8FB08C] text-[#1C1B1A] hover:bg-[#7CA079] flex items-center gap-1.5 font-medium">
                <Play size={14} weight="fill" /> Resume
              </button>
            ) : (
              <button onClick={pausePlay} data-testid="macro-shelf-pause"
                className="text-sm px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 flex items-center gap-1.5">
                <Pause size={14} weight="fill" /> Pause
              </button>
            )}
            <button onClick={stopPlay} data-testid="macro-shelf-stop"
              className="text-sm px-3 py-1.5 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85A48] flex items-center gap-1.5">
              <Stop size={14} weight="fill" /> Stop
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Heavy-hitter cost gate — premium-model warning before a run/batch. */}
      {heavyConfirm && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 px-4" data-testid="macro-heavy-confirm">
          <div className="modal-safe bg-[#FDFCF8] rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 rounded-full bg-[#FFF7E6] text-[#7A5200] flex items-center justify-center mx-auto mb-3">
              <Lightning size={24} weight="fill" />
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mb-1.5 text-center">Premium model — heads up</h3>
            <p className="text-sm text-[#6B6A68] mb-4 leading-relaxed text-center">
              “{heavyConfirm.macro.name}” runs on <span className="font-medium text-[#1C1B1A]">{heavyConfirm.model}</span>, a premium model, across <span className="font-medium text-[#1C1B1A]">{heavyConfirm.videos} video{heavyConfirm.videos === 1 ? "" : "s"}</span>. You're billed for every pass of every video.
              <span className="block mt-2 text-[#7A5200]">Rough estimate: <span className="font-medium" data-testid="macro-heavy-estimate">~${(heavyConfirm.estCents / 100).toFixed(2)}</span> (varies with transcript length). A cheaper "Hitters" model can do the same for a fraction — edit the Run step to switch.</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setHeavyConfirm(null)} data-testid="macro-heavy-cancel"
                className="flex-1 py-2.5 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A] text-sm">Cancel</button>
              <button onClick={() => { const mac = heavyConfirm.macro; setHeavyConfirm(null); runMacro(mac, { heavyConfirmed: true }); }} data-testid="macro-heavy-proceed"
                className="flex-1 py-2.5 rounded-md bg-[#7A5200] text-white hover:bg-[#5E3F00] text-sm font-medium">Run anyway</button>
            </div>
          </div>
        </div>
      )}

      {/* "Run complete" acknowledgement popup (single run) / batch summary. */}
      {doneModal && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 px-4" data-testid="macro-done-modal">
          <div className="modal-safe bg-[#FDFCF8] rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#EAF3EC] text-[#2F8F4E] flex items-center justify-center mx-auto mb-3">
              <Check size={24} weight="bold" />
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mb-1.5">{doneModal.title}</h3>
            <p className="text-sm text-[#6B6A68] mb-5 leading-relaxed">{doneModal.message}</p>
            <button onClick={() => setDoneModal(null)} data-testid="macro-done-ok"
              className="w-full py-2.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm font-medium">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
